import { Component } from '@theme/component';

/**
 * Language menu (`snippets/pc-language-menu.liquid`): a `details` whose card lists the languages of the store.
 *
 * The markup already works without this script (the `details` opens natively and each option is a submit button of Shopify's
 * `localization` form). The script adds what a menu is expected to do: Escape closes it and returns the focus to the summary,
 * a click outside or the focus leaving closes it, and the arrow keys, Home and End move between the options (ArrowDown on the
 * summary opens the menu). Choosing the language that is already shown just closes it instead of reloading the page.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The disclosure.
 * @property {HTMLButtonElement[]} [options] - One button per language.
 *
 * @extends {Component<Refs>}
 */
class PcLanguageMenu extends Component {
  requiredRefs = ['details'];

  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;
  }

  /**
   * Called by each option. The current language only closes the menu; any other one lets the form submit.
   * @param {Event} event
   */
  choose(event) {
    const option = event.target instanceof Element ? event.target.closest('[data-language]') : null;
    if (option?.getAttribute('aria-current') !== 'true') return;

    event.preventDefault();
    this.#close(true);
  }

  /** @param {boolean} [restoreFocus] Whether the focus goes back to the summary. */
  #close(restoreFocus = false) {
    const { details } = this.refs;
    if (!details.open) return;

    details.open = false;
    if (restoreFocus) details.querySelector('summary')?.focus();
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#close();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    // Leaving the menu with the keyboard closes it; moving between its own parts does not
    if (event.relatedTarget instanceof Node && !this.contains(event.relatedTarget)) this.#close();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const { details, options = [] } = this.refs;

    if (event.key === 'Escape' && details.open) {
      // The drawer and the dialogs around it close with Escape too: only the menu answers this one
      event.preventDefault();
      event.stopPropagation();
      this.#close(true);
      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || !options.length) return;

    const onSummary = event.target instanceof Element && event.target.closest('summary') !== null;
    if (!details.open && !(onSummary && event.key === 'ArrowDown')) return;

    event.preventDefault();
    if (!details.open) details.open = true;

    const index = options.findIndex((option) => option === document.activeElement);
    let next = 0;
    if (event.key === 'End') next = options.length - 1;
    else if (event.key === 'ArrowDown') next = index < 0 ? 0 : (index + 1) % options.length;
    else if (event.key === 'ArrowUp') next = index <= 0 ? options.length - 1 : index - 1;

    options[next]?.focus();
  };
}

if (!customElements.get('pc-language-menu')) {
  customElements.define('pc-language-menu', PcLanguageMenu);
}
